/**
 * VRG-545 — reading and formatting a `WEBSITE_FIELDS` value off a signup row.
 *
 * ## Why this is separate from both the manifest and the columns
 *
 * `websiteFields.ts` declares WHAT the site sends and is deliberately dependency-free —
 * the API-side drift guard (`signups-website-field-coverage.guard.spec.ts`) imports it
 * across the package boundary under ts-jest, so a React import there would break the one
 * check that catches vera-website drift.
 *
 * `websiteColumns.tsx` renders. This file is the bit in between: given a row and a field,
 * what is the value, and what does it read as. It is plain TS with no JSX, so the grid's
 * `valueGetter` (which must return a sortable/filterable primitive) and the detail panel
 * (which renders the same numbers as prose) cannot end up with two answers.
 *
 * That last point is not hypothetical tidiness. A grid cell showing `2.3%` beside a panel
 * showing `0.023` for the same field on the same row would be read as two different facts
 * about the visitor's business, on the one screen where someone is deciding whether to
 * believe a lead.
 *
 * ## The two unit traps, both of which have already produced a wrong render here
 *
 * 1. **Nesting.** The seven rate fields live under `roi_submission.assumptions`, not at the
 *    top level. A top-level read returns `undefined`, which renders an em-dash — and an
 *    em-dash says "the visitor left it blank", not "we looked in the wrong place". An
 *    earlier version of the panel reported no answers to the five questions the calculator
 *    is mostly made of, and typechecked while doing it.
 * 2. **Scale.** A rate is stored as a 0–1 RATIO and read as a PERCENT. Getting that
 *    backwards is a 100x misstatement. The unit is recorded per row (`assumptionsUnit`)
 *    rather than assumed, so a row written before the API normalised is still read
 *    correctly — see that field's doc on `RoiSubmission`.
 */
import type { PlanTierKey } from '@repo/types'

import { PLAN_TIER_TITLES } from 'constants/planComparison'
import type { RoiSubmission, SignupEntry, WaitlistSubmission } from 'hooks/api/useSignups'

import { ROI_INPUT_FIELDS, type WebsiteField } from './websiteFields'

export type AssumptionUnit = 'percent' | 'ratio'

/**
 * ISO 4217 code → the glyph to put in front of a money figure.
 *
 * A MIRROR of vera-website's `currencies` array (`content/roi.ts`), which is the closed set
 * its selector offers and the only set its route will accept — an unrecognised code is
 * coerced to USD there, so nothing outside this list can reach us through the UI.
 *
 * ⚠️ `CA$` and `A$` rather than a bare `$`, verbatim from that file and for its stated
 * reason: both sit in the same selector as USD, so a figure reading `$` on a row whose
 * currency is CAD is a screen contradicting itself. `CHF` has no glyph in use.
 *
 * A code we do not have a glyph for renders the CODE ITSELF (see `currencySymbolOf`), which
 * is unambiguous and is never wrong — the failure mode a symbol table has is showing the
 * wrong currency's symbol, not showing none.
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CHF: 'CHF',
  JPY: '¥',
  CAD: 'CA$',
  AUD: 'A$',
}

/**
 * What goes in front of `price`, `cac` and `total` ON THIS ROW.
 *
 * ── The USD fallback is correct, and must not be removed when more rows carry a code ─────
 *
 * vera-website asked for two money figures for months and recorded NO UNIT AT ALL. Every
 * ROI row written before its currency selector shipped is of genuinely unknown currency and
 * CANNOT BE BACKFILLED — the fact was never captured, so there is nothing to recover it
 * from. USD is the honest reading of that silence for three reasons the website's own
 * `DEFAULT_CURRENCY` note gives: it is the largest single market for that page's audience,
 * it is what every pre-selector submission was implicitly denominated in, and it is the
 * currency Vera itself is priced in.
 *
 * ⚠️ SO THIS IS A FALLBACK FOR HISTORY, NOT A DEFAULT FOR THE FUTURE, and deleting it once
 * "every row has a currency" would be a silent regression for exactly the rows that need it
 * most — the oldest leads, whose figures a reviewer is least able to sanity-check. It costs
 * nothing to keep and it is unreachable for any row the current site writes.
 *
 * ⚠️ AND NOTHING IS CONVERTED, HERE OR ANYWHERE. This puts a label on a figure that is
 * exactly what the visitor typed. A conversion with no rate and no rate DATE states a number
 * nobody computed; company-level conversion is VRG-577 and is a different subsystem.
 */
export const currencySymbolOf = (roi: RoiSubmission | null | undefined): string => {
  const code = typeof roi?.currency === 'string' ? roi.currency.trim().toUpperCase() : ''
  if (!code) return CURRENCY_SYMBOLS.USD
  // An unknown code prints as the code. A forged payload claiming "XYZ" then reads "XYZ
  // 1,250" — obviously odd — rather than borrowing a glyph it has no claim to.
  return CURRENCY_SYMBOLS[code] ?? `${code} `
}

/**
 * The unit THIS ROW's assumptions are stored in.
 *
 * `percent` for an undeclared row, because that is what the site sent before the API
 * started normalising — the honest reading of silence, and the same default
 * `normalizeAssumptions` applies on the way in.
 */
export const assumptionUnitOf = (roi: RoiSubmission | null | undefined): AssumptionUnit =>
  roi?.assumptionsUnit === 'ratio' ? 'ratio' : 'percent'

/** A stored rate → the number a human reads. `0.023` (ratio) → `2.3`. */
export const rateAsPercent = (value: number, unit: AssumptionUnit): number =>
  unit === 'ratio' ? value * 100 : value

/**
 * The value the GRID sorts and filters on — deliberately the same number the cell shows.
 *
 * ⚠️ Rates come back as PERCENTS (`2.3`), not ratios. The alternative — sorting on the
 * stored ratio while displaying a percent — sorts identically but filters wrongly: a
 * reviewer typing `> 2` against a column reading `2.3%` would match nothing, and a filter
 * that silently returns the wrong set is the failure mode this whole surface is being
 * built to avoid. Display and filter read one number.
 *
 * A `list` collapses to its joined string so the grid's `contains` operator works on it;
 * the cell renders from this same string, so what you filter is what you see.
 *
 * `null` means absent — no submission, or the field is not on the blob. Never `undefined`:
 * MUI treats the two the same for filtering, but `null` is the value this module promises
 * so callers can test one thing.
 */
export const websiteSortValue = (
  row: SignupEntry,
  field: WebsiteField,
): number | string | null => {
  const blob: RoiSubmission | WaitlistSubmission | null =
    field.group === 'roi' ? row.roi_submission : row.waitlist_submission
  if (!blob) return null

  const raw = field.nested === 'assumptions' ? assumptionRaw(blob as RoiSubmission, field.id) : blob[field.id]
  if (raw === null || raw === undefined) return null

  switch (field.kind) {
    case 'rate': {
      const n = toFiniteNumber(raw)
      return n === null ? null : rateAsPercent(n, assumptionUnitOf(row.roi_submission))
    }
    case 'number':
    case 'currency':
      return toFiniteNumber(raw)
    case 'list': {
      // A non-array here means the site changed shape. Rendering the scalar is more
      // truthful than dropping it — the reviewer sees something wrong rather than
      // nothing, which is the difference between a bug report and a silent gap.
      if (!Array.isArray(raw)) return typeof raw === 'string' ? raw || null : String(raw)
      const items = raw.filter((v): v is string => typeof v === 'string' && v.trim() !== '')
      return items.length ? items.join(', ') : null
    }
    case 'text': {
      const text = typeof raw === 'string' ? raw.trim() : String(raw)
      if (!text) return null
      /*
        ⚠️ THE LABEL IS RESOLVED HERE, IN THE VALUE — not in the cell.

        `planInterest` arrives as a tier SLUG (`business`), and the column's filter is a
        `multiSelect` facet list built from this same value. Resolving the label in
        `renderCell` instead would give a cell reading "Business" and a filter checkbox
        reading "business" for the same rows — the display/filter disagreement this
        module's header calls the failure mode of the whole surface. One string, read by
        the cell, the sort, the facet and the CSV export.
      */
      return field.id === 'planInterest' ? planInterestLabel(text) : text
    }
  }
}

/**
 * A plan-interest tier slug → what the visitor saw on the picker.
 *
 * ⚠️ DERIVED FROM THE CANONICAL TIER LIST, never from capitalising the slug. `PLAN_TIER_KEYS`
 * is a closed set whose members are renamed for product reasons — `enterprise` was renamed to
 * `scale` in 2026-08 precisely because the word was needed for something else — and a
 * `charAt(0).toUpperCase()` would keep rendering a plausible title for a tier that no longer
 * goes by it. `PLAN_TIER_TITLES` is typed TOTAL over the union, so a new tier is a compile
 * error rather than a row that reads correctly by luck.
 *
 * ⚠️ `enterprise` IS A REAL VALUE HERE AND HAS NO STRIPE PRICE. It is the contact-sales tier,
 * so this is an expression of interest and not a purchase intent that any checkout can
 * satisfy — nothing downstream of this label may treat it as a plan that can be bought.
 *
 * An unrecognised value renders verbatim rather than blank, on the same grounds as an
 * unrecognised category: vera-website allowlists this field against `PLAN_TIER_KEYS` before
 * forwarding, so a value that reaches us and is not in the list means the two lists have
 * drifted — which a reviewer should SEE rather than have hidden behind an em-dash.
 */
const planInterestLabel = (value: string): string =>
  PLAN_TIER_TITLES[value.trim().toLowerCase() as PlanTierKey] ?? value

/**
 * A sort value → the string the cell shows.
 *
 * Takes the value rather than the row on purpose: the cell renders whatever the grid gave
 * it, so a `valueGetter`/`renderCell` disagreement is structurally impossible rather than
 * merely avoided by convention.
 *
 * `maximumFractionDigits` is 4 on a rate because a slider at `0.0125` (1.25%) must not be
 * rounded into a different number by the thing displaying it, and 2 on money because a
 * price is a price.
 */
export const formatWebsiteValue = (
  field: WebsiteField,
  value: unknown,
  /**
   * The glyph for a `currency` field, from `currencySymbolOf(row.roi_submission)`.
   *
   * ⚠️ REQUIRED, AND DELIBERATELY NOT DEFAULTED TO `'$'`. A default would make the correct
   * call site and the forgetful one look identical, and the forgetful one would print
   * dollars on a euro row — a wrong fact rendered confidently, which is the failure this
   * whole module is organised against. The compiler asks the question instead.
   */
  currencySymbol: string,
): string | null => {
  if (value === null || value === undefined || value === '') return null

  if (field.kind === 'rate') {
    const n = toFiniteNumber(value)
    if (n === null) return null
    return `${n.toLocaleString('en-US', { maximumFractionDigits: 4 })}%`
  }

  if (field.kind === 'number' || field.kind === 'currency') {
    const n = toFiniteNumber(value)
    if (n === null) return null
    const shown = n.toLocaleString('en-US', { maximumFractionDigits: 2 })
    /*
      The symbol is PREFIXED and comes from the row (`roi_submission.currency`), never from
      a literal here. The visitor typed the figure under a currency they chose on
      vera-website, so the unit is a recorded fact about the submission rather than an
      assumption about a stranger's business — which is what the earlier no-symbol rule was
      protecting against, correctly, at a time when the site recorded no unit at all.

      A `number` field gets no symbol: it is a count.
    */
    return field.kind === 'currency' ? `${currencySymbol}${shown}` : shown
  }

  return String(value)
}

/**
 * The website's seeded default, formatted the way the column shows a value.
 *
 * Money defaults are shown in USD, and this is the ONE place a literal is right: the figure
 * is the SITE's starting value, not a visitor's answer, and vera-website's own
 * `DEFAULT_CURRENCY` is `"USD"` — the selector opens on it. A per-row symbol would be wrong
 * here in a specific way: this string appears in a COLUMN HEADER's tooltip, which is one
 * tooltip for every row, so it cannot be denominated in any one row's currency.
 */
export const formatWebsiteDefault = (field: WebsiteField): string | null => {
  if (field.websiteDefault === null) return null
  // The manifest stores rate defaults as RATIOS (the guard asserts it), so they take the
  // same ×100 the stored values take before formatting.
  const asShown = field.kind === 'rate' ? field.websiteDefault * 100 : field.websiteDefault
  return formatWebsiteValue(field, asShown, CURRENCY_SYMBOLS.USD)
}

/**
 * Whether this cell's value is the website's seeded starting value or the visitor's own
 * answer — and HOW WELL WE KNOW IT, which is the part that carries the weight.
 *
 * ## Four states, because there are four genuinely different situations
 *
 * | state | means | rendered as |
 * |---|---|---|
 * | `changed`          | the visitor MOVED this control. Recorded, not inferred.  | nothing — it is their answer |
 * | `default`          | the visitor did NOT move it. Recorded, not inferred.     | a "default" tag |
 * | `default-inferred` | not recorded; the value happens to EQUAL our default.    | a weaker "matches default" tag |
 * | `unknown`          | not recorded, and the value differs from our default.    | nothing |
 *
 * ⚠️ `changed` AND `default-inferred` CAN CARRY THE SAME NUMBER, and that is precisely why
 * the two exist. A visitor who deliberately lands on 20% and one who never touched the
 * slider produce identical stored values; only `assumptionsTouched` can tell them apart,
 * and where it is present it MUST win over the comparison. Collapsing them would report
 * "we told them 20% and they didn't argue" about someone who typed it.
 *
 * ## Why the comparison is a FALLBACK and never the primary signal
 *
 * Testing a stored value against `websiteDefault` is a guess, for two compounding reasons:
 * it cannot see a deliberate choice of the default (above), and it rots silently the day
 * vera-website changes one — this manifest holds the old figure until somebody notices.
 * That is not hypothetical: `resaleBuyerConversion` moved 5% → 28% in the same week this
 * was written, which would have reclassified every stored row at a stroke.
 *
 * So the comparison is reached ONLY when nothing better exists, and what it produces is
 * labelled as the weaker claim it is (`default-inferred`, "matches default") rather than
 * being laundered into the strong one. The alternative considered and rejected was to keep
 * returning `'unknown'` for those rows: that throws away a real, if soft, signal on the one
 * screen whose entire job is separating a visitor's answer from ours — and rows predating
 * `assumptionsTouched` are the OLDEST leads, exactly the ones a reviewer can least
 * sanity-check from memory.
 *
 * ## Scope: assumption sliders, plus `units`, and the asymmetry is deliberate
 *
 * `assumptionsTouched` is named for the assumptions and enumerates them, so reading a
 * missing `units` out of it as "untouched" would be inventing a signal from a list that
 * never claimed to cover it. Hence `touchedCoversField`: the recorded signal applies to a
 * nested assumption, or to any field the site DOES happen to name in that list (harmless,
 * and it picks up a future widening automatically). The comparison fallback applies to any
 * field with a declared `websiteDefault`, which is what lets the potential-revenue column
 * refuse to compute from a `units` of 25,000 nobody typed.
 */
export type DefaultState = 'default' | 'default-inferred' | 'changed' | 'unknown'

/**
 * Float equality for a stored rate against a manifest default.
 *
 * `0.023 !== 0.023` is not the failure mode here — both sides are decimal literals that
 * round-trip through JSON — but `0.1 + 0.2` arithmetic upstream, or a site that starts
 * sending `2.2999999999999998`, is. A tolerance of 1e-9 is far tighter than any real
 * slider step (the finest is 0.1 of a percent, i.e. 0.001 as a ratio) so it cannot
 * accidentally equate two DIFFERENT answers, and loose enough to survive representation.
 */
const RATE_EPSILON = 1e-9

/** Whether the recorded `assumptionsTouched` list is authoritative for this field. */
const touchedCoversField = (touched: string[], field: WebsiteField): boolean =>
  field.nested === 'assumptions' || touched.includes(field.id)

export const websiteDefaultState = (row: SignupEntry, field: WebsiteField): DefaultState => {
  const roi = row.roi_submission
  const touched = roi?.assumptionsTouched

  // THE RECORDED SIGNAL, and it wins outright wherever it applies — including when the
  // stored value equals our default, which is the whole point of preferring it.
  if (Array.isArray(touched) && touchedCoversField(touched, field)) {
    return touched.includes(field.id) ? 'changed' : 'default'
  }

  // No recorded signal. Fall back to the comparison, and label what it yields honestly.
  if (field.websiteDefault === null) return 'unknown'

  const stored = websiteSortValue(row, field)
  if (typeof stored !== 'number') return 'unknown'

  // `websiteSortValue` returns a rate as a PERCENT while the manifest stores the default
  // as a RATIO, so one side has to be converted. Getting this backwards would compare
  // 2.3 against 0.023 and report every rate as differing — a marker that never fires
  // looks exactly like a marker that has nothing to report.
  const seeded = field.kind === 'rate' ? field.websiteDefault * 100 : field.websiteDefault
  return Math.abs(stored - seeded) < RATE_EPSILON ? 'default-inferred' : 'unknown'
}

/**
 * Is this field's value demonstrably THEIRS rather than ours? The gate every derived
 * revenue figure on this grid has to pass before it is allowed to exist.
 *
 * ## Why this is a named predicate and not `state !== 'default'` at each call site
 *
 * It is a THREE-way test written as a two-way one, and getting it wrong fails open. The
 * refusable states are `default` (recorded: they never moved the control) AND
 * `default-inferred` (not recorded, and the value equals our seeded figure) — so the
 * plausible shorthand `!== 'default'` admits every inferred-default row, which is exactly
 * the population this gate exists for. There is no error and nothing on screen: a large,
 * specific number derived from vera-website's own starting values appears beside a real
 * person's name, and nobody reading the column can tell it was ours.
 *
 * `unknown` and `changed` both pass. `changed` is their recorded answer; `unknown` means
 * we have no signal AND the value differs from our default, which is the strongest
 * evidence available for a row predating `assumptionsTouched`.
 *
 * ⚠️ EVERY input of a derived figure must pass, not the headline one. A figure built from
 * three of the visitor's numbers and one of ours is still partly furniture, and the
 * fraction is invisible in the output. See `brokerageRevenue.ts`, which needs four.
 *
 * Extracted 2026-09-08 (§36 — the second site was about to hand-roll the same two-state
 * comparison `potentialRevenue.ts` already had inline).
 */
export const isVisitorSupplied = (row: SignupEntry, field: WebsiteField): boolean => {
  const state = websiteDefaultState(row, field)
  return state !== 'default' && state !== 'default-inferred'
}

/**
 * HOW MANY OF THE CALCULATOR'S TEN FIELDS THE VISITOR ACTUALLY FILLED IN — and how much of
 * that count is a recorded fact rather than an inference.
 *
 * ## Why a split and not just a number
 *
 * The two halves of the denominator are known with genuinely different confidence, and a
 * bare "6/10" would present them as one thing:
 *
 * | half | signal | strength |
 * |---|---|---|
 * | the seven assumption sliders | `assumptionsTouched` names the ones the visitor moved | RECORDED |
 * | `units`, `price`, `cac` | the stored value differs from our seeded default | INFERRED |
 *
 * vera-website's `assumptionsTouched` is allowlisted against its declared slider ids
 * (`ASSUMPTION_IDS` in that repo's `app/api/roi/route.ts`), so it can never carry `units`,
 * `price` or `cac` — which is why the top-line three fall to the comparison and why
 * `touchedCoversField` is written the way it is. The inference cannot see a visitor who
 * deliberately typed 25,000, and it rots the day the site changes a default. So the count
 * carries its own provenance to the tooltip rather than laundering the weaker half into the
 * stronger one — the same discipline `DEFAULT_TAGS` applies one cell at a time.
 *
 * ⚠️ `cac`'s inclusion in `inferred` UNDERSTATES what we know, deliberately. The site sends
 * `null` rather than its 0 default for an unanswered CAC (`cac === 0 ? null : cacRaw` in
 * that route), so a present non-zero CAC really is a recorded answer. It is counted as
 * inferred anyway, because the alternative is a per-id carve-out in this function that
 * would then have to be kept in step with another repo's route by hand — and understating
 * confidence is the safe direction for a figure whose entire job is telling a reviewer how
 * much to trust it.
 *
 * ## `null` means "do not show a count", and there are two ways to get there
 *
 * No submission at all, or a submission with NO `assumptionsTouched` key (rows predating
 * the site sending it). The second is the one worth naming: falling back to the comparison
 * for all ten would produce a number that looks identical to a real one while resting
 * entirely on guesswork, on a screen whose whole purpose is separating the visitor's
 * answers from ours. The column renders its old "Submitted" text there instead.
 *
 * ⚠️ `[]` IS NOT ABSENT. An empty array is vera-website's real answer meaning "moved
 * nothing" (its `forwardRoi` comment says so in as many words), and it yields a legitimate
 * count in which no slider is recorded as changed. Only a MISSING key is uncountable.
 *
 * ## The gate is `isVisitorSupplied`, called rather than reimplemented
 *
 * A field counts when it is both PRESENT and visitor-supplied. The presence test is not
 * redundant with the gate: `isVisitorSupplied` fails OPEN on `unknown` — which is correct
 * for its own job, refusing to price a derived figure — and an absent value reads as
 * `unknown` too, so the gate alone would count a blank CAC as an answer.
 */
export interface RoiInputTally {
  /** How many of `total` the visitor supplied. `recorded + inferred`. */
  supplied: number
  /** The denominator. Every field the calculator offers; see `ROI_INPUT_FIELDS`. */
  total: number
  /** Of `supplied`, the ones the submission RECORDS as moved. */
  recorded: number
  /** Of `supplied`, the ones read as theirs only because they differ from our default. */
  inferred: number
}

export const roiInputsSupplied = (row: SignupEntry): RoiInputTally | null => {
  const roi = row.roi_submission
  if (!roi) return null
  if (!Array.isArray(roi.assumptionsTouched)) return null

  let recorded = 0
  let inferred = 0

  for (const field of ROI_INPUT_FIELDS) {
    // Absent is not an answer. Checked FIRST, because the gate below cannot distinguish
    // "they left it blank" from "we have no signal and it differs from our default".
    if (websiteSortValue(row, field) === null) continue
    if (!isVisitorSupplied(row, field)) continue
    // `changed` or `unknown` by construction — the gate has already refused the other two.
    if (websiteDefaultState(row, field) === 'changed') recorded += 1
    else inferred += 1
  }

  return { supplied: recorded + inferred, total: ROI_INPUT_FIELDS.length, recorded, inferred }
}

/**
 * A `list` field's INDIVIDUAL items — the facets, not the joined cell text.
 *
 * The cell and the sort value are one string ("handbags, leather-goods") because a grid cell
 * is one line and a sort needs one comparable. A FILTER wants the opposite: "show me everyone
 * who picked Watches" has to match a row whose other answers differ. So the filter resolves
 * through here instead, and the column's `isAnyOf` operator tests membership rather than
 * string equality — otherwise every distinct COMBINATION of answers is its own option and
 * ticking one matches only the visitors who picked exactly that set.
 *
 * `text` and numeric fields have no such split and do not use this.
 */
export const websiteListItems = (row: SignupEntry, field: WebsiteField): string[] => {
  if (field.kind !== 'list') return []
  const blob: RoiSubmission | WaitlistSubmission | null =
    field.group === 'roi' ? row.roi_submission : row.waitlist_submission
  const raw = blob?.[field.id]
  if (Array.isArray(raw)) {
    return raw.filter((v): v is string => typeof v === 'string' && v.trim() !== '')
  }
  // Same shape-change tolerance as `websiteSortValue`: a scalar where an array was expected
  // is surfaced rather than dropped.
  return typeof raw === 'string' && raw.trim() ? [raw] : []
}

/** `resaleValueRatio` → `Resale value ratio`. For keys we have no curated label for. */
export const humanizeKey = (key: string): string =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase())

const assumptionRaw = (roi: RoiSubmission, id: string): unknown => {
  const assumptions = roi.assumptions
  if (!assumptions || typeof assumptions !== 'object' || Array.isArray(assumptions)) return undefined
  return (assumptions as Record<string, unknown>)[id]
}

/**
 * `NaN`/`Infinity` survive `JSON.parse` only as strings, but a hand-edited blob could
 * still carry one, and a non-finite number formatted with `toLocaleString` renders "NaN"
 * inside a column of real figures. Absent is the truthful rendering of unusable.
 */
const toFiniteNumber = (value: unknown): number | null => {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}
